// Top-level container for the Settings > Integrations > Sales
// Channels section. Loads /admin/integrations and renders each
// channel as a collapsible card. The whole "Sales channels" group is
// itself a collapsible dropdown so other integration categories can
// sit alongside it cleanly in future.

import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import ChannelCard from "./ChannelCard";
import ShopifyStoreCard from "./ShopifyStoreCard";
import AmazonChannelCard from "./AmazonChannelCard";
import AddSalesChannelModal from "./AddSalesChannelModal";
import type { SalesChannelConfig } from "./types";
import "../../../styles/admin-integrations.css";

const SalesChannelsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [channels, setChannels] = useState<SalesChannelConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await apiFetch("/admin/integrations", {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setChannels(Array.isArray(json?.channels) ? json.channels : []);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setLoadError(msg);
            toast.error(t("integrations_load_failed"), { toastId: "integrations-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const handleChannelUpdated = useCallback((next: SalesChannelConfig) => {
        setChannels(curr => curr.map(c => (c.id === next.id ? next : c)));
    }, []);

    const handleStoreCreated = useCallback((next: SalesChannelConfig) => {
        setChannels(curr => [...curr, next]);
    }, []);

    const handleStoreDeleted = useCallback((channelCode: string) => {
        setChannels(curr => curr.filter(c => c.channelCode !== channelCode));
    }, []);

    const jobalots   = channels.find((c) => c.channelCode === "jobalots")   ?? null;
    const bargainfox = channels.find((c) => c.channelCode === "bargainfox") ?? null;
    // Every connected store, newest last (creation order). Matched on
    // vendorFamily rather than a code prefix — the code is derived from
    // the shop domain and is not a stable thing to pattern-match on.
    const shopifyStores = channels
        .filter((c) => c.vendorFamily === "shopify")
        .sort((a, b) => a.id - b.id);
    // One card per connected marketplace. A seller on amazon.co.uk and
    // amazon.de has two: different listings, different order streams,
    // and each routed to its own warehouse.
    const amazonChannels = channels
        .filter((c) => c.vendorFamily === "amazon")
        .sort((a, b) => a.id - b.id);

    return (
        <details className="admin-integrations-group">
            <summary className="admin-integrations-group-summary">
                <span className="admin-integrations-group-title">
                    {t("integrations_sales_channels_heading")}
                </span>
                <span className="admin-integrations-group-count">
                    {channels.length}
                </span>
            </summary>

            <div className="admin-integrations-group-body">
                {loading ? (
                    <div className="admin-integrations-loading">
                        <Loader2 size={18} className="admin-integrations-spinner" /> {t("loading")}
                    </div>
                ) : loadError ? (
                    <p className="admin-integrations-error">{loadError}</p>
                ) : (
                    <div className="admin-integrations">
                        {/* One entry point for every platform. Credentials
                            are proven inside the wizard before anything is
                            written, so a bad paste never leaves a
                            half-configured channel behind. */}
                        <div className="admin-integrations-add-channel-row">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setAddOpen(true)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Plus size={14} /> {t("add_channel_title")}
                            </button>
                        </div>
                        {jobalots && (
                            <ChannelCard
                                channel={jobalots}
                                hint={t("integrations_jobalots_hint")}
                                onUpdated={handleChannelUpdated}
                            />
                        )}
                        {bargainfox && (
                            <ChannelCard
                                channel={bargainfox}
                                hint={t("integrations_bargainfox_hint")}
                                onUpdated={handleChannelUpdated}
                            />
                        )}
                        {shopifyStores.map((store) => (
                            <ShopifyStoreCard
                                key={store.id}
                                channel={store}
                                onUpdated={handleChannelUpdated}
                                onDeleted={handleStoreDeleted}
                            />
                        ))}
                        {amazonChannels.map((ch) => (
                            <AmazonChannelCard
                                key={ch.id}
                                channel={ch}
                                onUpdated={handleChannelUpdated}
                                onDeleted={handleStoreDeleted}
                            />
                        ))}
                    </div>
                )}
            </div>
            {addOpen && (
                <AddSalesChannelModal
                    onCreated={handleStoreCreated}
                    onClose={() => setAddOpen(false)}
                />
            )}
        </details>
    );
};

export default SalesChannelsAdmin;
